/**
 *  @swagger
 *  definitions:
 *    BannerParams:
 *      type: object
 *      properties:
 *        Public:
 *          description: Public of the Banner
 *          type: boolean
 *        Name:
 *          description: Name of the Banner
 *          type: string
 *        ImageLG:
 *          description: Image LG of the Banner
 *          type: string
 *        ImageMD:
 *          description: Image MD of the Banner
 *          type: string
 *        ImageSM:
 *          description: Image SM of the Banner
 *          type: string
 *        ImageXS:
 *          description: Image XS of the Banner
 *          type: string
 *        ButtonClass:
 *          description: Button Class of the Banner
 *          type: string
 *        ButtonLink:
 *          description: Button Link of the Banner
 *          type: string
 *        ButtonName:
 *          description: Button Name of the Banner
 *          type: string
 *        ContainerClass:
 *          description: Container Class of the Banner
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    BannerCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/BannerParams'
 *        - type: object
 *          required:
 *            - Name
 *            - ImageLG
 *            - ImageMD
 *            - ImageSM
 *            - ImageXS
 *            - Public
 *
 *  @swagger
 *  definitions:
 *    BannerQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Banner
 *              type: string
 *        - $ref: '#/definitions/BannerParams'
 *
 *  @swagger
 *  definitions:
 *    BannerUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/BannerQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Banner:
 *      allOf:
 *        - $ref: '#/definitions/BannerCreateParams'
 *        - $ref: '#/definitions/BannerUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Banner:
 *        allOf:
 *          - $ref: '#/definitions/Banner'
 *          - type: object
 *            properties:
 *              BannerLocations:
 *                description: BannerLocations of the Banner
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/BannerLocation'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $Params = {
  ButtonClass: null | string;
  ButtonLink: null | string;
  ButtonName: null | string;
  ContainerClass: null | string;
  ImageLG: string;
  ImageMD: string;
  ImageSM: string;
  ImageXS: string;
  Name: string;
  Public: boolean;
};

export type $CreateParams = $Params;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$Params>;

export type $Associations = {
  BannerLocations?: Array<$Model<'BannerLocation'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  ButtonClass: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ButtonLink: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ButtonName: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ContainerClass: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  ImageLG: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ImageMD: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ImageSM: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ImageXS: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Banner'> => {
  const Banner = stack.define(
    'Banner',
    fields(Sequelize),
    {
      tableName: 'Banners',
    },
  );

  Banner.associate = (models: $ModelClasses) => {
    models.Banner.hasMany(
      models.BannerLocation,
      {
        as: 'BannerLocations',
        foreignKey: 'BannerId',
      },
    );
  };

  Banner.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return Banner;
});
